import {
  ACCOUNT_REQUIRED_MESSAGE,
  RACE_MAX_STAKE_PER_BET,
  RACE_MAX_STAKE_PER_RACE,
  RACE_HORSE_COUNT,
  formatRaceOdds,
  type RaceHorse,
  type RacingPrivate,
  type RacingState,
  type SilkPattern,
} from "@townsy/contracts";

/**
 * Townsy Downs on the client (ADR-0016): pure helpers that turn the server's race state into what the overlay and the
 * tote board show. Nothing here decides an outcome.
 */

/** Saddle-cloth colours by number (the usual racecourse convention): cloth and number colour. */
export const SADDLE_CLOTH: Record<number, { cloth: string; text: string }> = {
  1: { cloth: "#D7262B", text: "#FFFFFF" },
  2: { cloth: "#F4F4F2", text: "#1B1F2A" },
  3: { cloth: "#1F4BB8", text: "#FFFFFF" },
  4: { cloth: "#F4C914", text: "#1B1F2A" },
  5: { cloth: "#1E8A3C", text: "#FFFFFF" },
  6: { cloth: "#1B1B1B", text: "#F4C914" },
  7: { cloth: "#F07F1A", text: "#1B1F2A" },
  8: { cloth: "#F49AC1", text: "#1B1F2A" },
};

/** A shared state that looks like the race track's (anything else is ignored). */
export function asRacingState(shared: unknown): RacingState | null {
  if (!shared || typeof shared !== "object") return null;
  const s = shared as Partial<RacingState>;
  if (typeof s.raceId !== "number" || !Array.isArray(s.horses) || typeof s.phase !== "string") return null;
  return s as RacingState;
}

export function asRacingPrivate(mine: unknown): RacingPrivate | null {
  if (!mine || typeof mine !== "object") return null;
  const m = mine as Partial<RacingPrivate>;
  if (typeof m.raceId !== "number" || !Array.isArray(m.bets)) return null;
  return m as RacingPrivate;
}

/** Horse numbers in running order: finished horses in crossing order, then by distance run (ties: lower number). */
export function liveOrder(state: Pick<RacingState, "horses" | "progress" | "finish">): number[] {
  const done = state.finish.map((f) => f.n);
  const seen = new Set(done);
  const rest = state.horses
    .map((h) => h.n)
    .filter((n) => !seen.has(n))
    .sort((a, b) => (state.progress ? (state.progress[b - 1] ?? 0) - (state.progress[a - 1] ?? 0) : 0) || a - b);
  return [...done, ...rest];
}

export function ordinal(n: number): string {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
}

/** Whole seconds left in the phase, counting down locally from when the state arrived. */
export function secondsLeft(state: Pick<RacingState, "endsIn">, receivedAtMs: number, nowMs: number): number {
  return Math.max(0, Math.ceil((state.endsIn - Math.max(0, nowMs - receivedAtMs)) / 1000));
}

export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function horseLabel(h: Pick<RaceHorse, "n" | "name">): string {
  return `#${h.n} ${h.name}`;
}

/** The status line above the race card. */
export function headline(state: RacingState, seconds: number): { title: string; detail: string } {
  const byN = (n: number) => state.horses[n - 1];
  switch (state.phase) {
    case "betting":
      return { title: `Race ${state.raceId}: place your bets`, detail: `They're off in ${formatClock(seconds)}` };
    case "racing": {
      const leader = byN(liveOrder(state)[0] ?? 1);
      if (state.finish.length > 0) {
        const first = byN(state.finish[0]!.n);
        return { title: `Race ${state.raceId}: ${first ? horseLabel(first) : ""} first past the post!`, detail: "Waiting for the rest of the field" };
      }
      return { title: `Race ${state.raceId}: they're off!`, detail: leader ? `Leading: ${horseLabel(leader)}` : "" };
    }
    case "results": {
      const r = state.result;
      const win = r ? byN(r.winner.n) : undefined;
      return {
        title: r && win ? `Race ${state.raceId}: ${horseLabel(win)} wins at ${formatRaceOdds(r.winner.odds)}` : `Race ${state.raceId}: results`,
        detail: `Next race in ${formatClock(seconds)}`,
      };
    }
  }
}

/** Largest stake a new bet may have now (0 = none). */
export function maxStake(chips: number | null, mine: RacingPrivate | null): number {
  const staked = mine ? mine.bets.reduce((s, b) => s + b.stake, 0) : 0;
  return Math.max(0, Math.min(chips ?? 0, RACE_MAX_STAKE_PER_BET, RACE_MAX_STAKE_PER_RACE - staked));
}

/** Why "Place bet" is unavailable right now ("" when a bet can be placed). */
export function betBlocker(args: {
  state: RacingState | null;
  mine: RacingPrivate | null;
  chips: number | null;
  chanceGamesEnabled: boolean;
  /** A guest (ADR-0048: betting needs an account; the server refuses a guest's bet with ACCOUNT_REQUIRED). */
  guest?: boolean;
  horse: number | null;
  stake: number;
  zoneText: string;
}): string {
  const { state, mine, chips, chanceGamesEnabled, horse, stake, zoneText } = args;
  if (!chanceGamesEnabled) return "Betting is switched off here: watch the races and sit in the stands.";
  if (args.guest) return ACCOUNT_REQUIRED_MESSAGE;
  if (!state || !mine) return "Connecting to the race track...";
  if (state.phase !== "betting" || !state.bettingOpen) return "Betting is closed: bets open again for the next race.";
  if (!mine.canBet) return zoneText;
  if (chips === null) return "Getting your play chips...";
  if (chips <= 0) return "You're out of play chips. Walk out and back in to get 100.";
  const staked = mine.bets.reduce((s, b) => s + b.stake, 0);
  if (staked >= RACE_MAX_STAKE_PER_RACE) return `You've bet the most allowed on this race (${RACE_MAX_STAKE_PER_RACE} play chips).`;
  if (horse === null || horse < 1 || horse > RACE_HORSE_COUNT) return "Pick a horse, then a stake.";
  const max = maxStake(chips, mine);
  if (stake > max) return `Pick a smaller stake: up to ${max} play chips now.`;
  return "";
}

/** CSS background for a jockey's silks swatch. */
export function silkCss(silk: { body: string; trim: string; pattern: SilkPattern }): string {
  const { body, trim } = silk;
  switch (silk.pattern) {
    case "hoops":
      return `repeating-linear-gradient(180deg, ${body} 0 25%, ${trim} 25% 50%)`;
    case "halves":
      return `linear-gradient(90deg, ${body} 50%, ${trim} 50%)`;
    case "sash":
      return `linear-gradient(135deg, ${body} 38%, ${trim} 38% 62%, ${body} 62%)`;
    case "stars":
      return `radial-gradient(circle at 30% 32%, ${trim} 16%, transparent 18%), radial-gradient(circle at 70% 70%, ${trim} 16%, transparent 18%), ${body}`;
    case "quarters":
      return `conic-gradient(${body} 0 25%, ${trim} 0 50%, ${body} 0 75%, ${trim} 0)`;
    case "solid":
    default:
      return body;
  }
}

/** The seat index a visitor sits in (from the shared seat list), or null. */
export function seatOf(state: Pick<RacingState, "seats"> | null, actorId: string | null): number | null {
  if (!state || !actorId) return null;
  const hit = state.seats.find(([, id]) => id === actorId);
  return hit ? hit[0] : null;
}
